/**
 * Structured error parser for the correspondence-api facade.
 *
 * The sibling `getSupabaseFunctionError` in `supabase-utils.ts` returns a
 * best-effort string for any Supabase edge-function failure. Phase 4's
 * typed hooks need more — `code`, `httpStatus`, `provider`, `action`,
 * `details` — so they can throw `CorrespondenceApiError` /
 * `UnsupportedOnProviderError` with full fidelity.
 *
 * Lives in its own module (not inside `supabase-utils.ts`) because that
 * file carries a pre-existing file-level `eslint-disable` for the
 * legacy-any code the repo's pre-commit autofix would otherwise strip.
 * Keeping this new, strict-typed helper separate lets it satisfy the
 * typeaware lint config without any disables.
 *
 * Phase 6 will consolidate `getSupabaseFunctionError` + this helper once
 * all callers migrate to the structured shape.
 */

import { AuthApiError, FunctionsHttpError } from '@supabase/supabase-js';

import { getErrorMessage } from '@/lib/supabase-utils';

import type { CorrespondenceAction, Provider } from '@/lib/correspondence/contracts';
import type { CorrespondenceErrorCode } from '@/lib/errors/correspondence-api-error';

export interface StructuredFunctionError {
  readonly message: string;
  readonly code: CorrespondenceErrorCode;
  readonly httpStatus: number;
  readonly provider?: Provider;
  readonly action?: CorrespondenceAction;
  readonly details?: Record<string, unknown>;
}

/**
 * Parses a Supabase `FunctionsHttpError` response into the structured
 * envelope the `useCorrespondenceQuery` / `useCorrespondenceMutation`
 * hooks consume. Reads the response body at most once (clones first so a
 * downstream consumer still sees the body — Response streams can only be
 * consumed once).
 *
 * Non-facade errors (legacy endpoints returning `{error: string}`,
 * plain-text 5xx, network failures) collapse to a best-effort shape:
 * `{message: "<text>", code: 'INTERNAL_ERROR', httpStatus: <status or 0>}`.
 */
export async function getSupabaseFunctionErrorStructured(
  error: unknown,
): Promise<StructuredFunctionError> {
  if (error instanceof FunctionsHttpError) {
    return await parseFunctionsHttpError(error);
  }

  return {
    message: getErrorMessage(error),
    code: 'INTERNAL_ERROR',
    httpStatus: extractHttpStatus(error),
  };
}

/**
 * Hard ceiling on the bytes we'll buffer for an error body. A misbehaving
 * upstream (proxy, Vault, Deno runtime) that returns a multi-MB error
 * response shouldn't cause the client to allocate all of it — we only ever
 * keep a 500-char excerpt anyway. 64 KB is generous for any realistic
 * error envelope.
 */
const MAX_ERROR_BODY_BYTES = 64 * 1024;

/** Bound on the reflected rawBody excerpt surfaced for ops debugging. */
const RAW_BODY_EXCERPT_MAX = 500;

async function parseFunctionsHttpError(
  error: FunctionsHttpError,
): Promise<StructuredFunctionError> {
  const rawResponse: unknown = error.context;
  const response =
    isResponseLike(rawResponse) && typeof rawResponse.clone === 'function'
      ? rawResponse.clone()
      : rawResponse;

  let httpStatus = 0;
  let bodyText = '';
  if (isResponseLike(response)) {
    httpStatus = response.status ?? 0;
    const declaredSize = Number.parseInt(response.headers?.get('content-length') ?? '', 10);
    if (!Number.isFinite(declaredSize) || declaredSize <= MAX_ERROR_BODY_BYTES) {
      try {
        bodyText = await response.text();
      } catch {
        // Body stream already consumed or network torn down — fall through
        // to the bare envelope.
      }
    }
    // Else: declared body is too large; skip the read entirely. We still
    // have `httpStatus` and the Supabase-provided `error.message`.
  }

  const envelope = parseFacadeEnvelope(bodyText);
  if (envelope) {
    return { ...envelope, httpStatus };
  }

  // Parse-failure fallback. Collapsing to a bounded generic message keeps
  // `error.message` safe for Sentry / toasts — any request-fragment-
  // reflecting upstream (CDN, WAF, proxy) gets a bounded envelope here.
  // Raw body is still available under `details.rawBody` for ops dashboards
  // that explicitly opt in; by convention this field is NEVER surfaced to
  // toasts/logs without a caller-level allowlist.
  const excerpt = bodyText ? bodyText.slice(0, RAW_BODY_EXCERPT_MAX) : undefined;
  return {
    message: `correspondence-api returned an unrecognised error envelope (HTTP ${httpStatus || 'unknown'})`,
    code: 'INTERNAL_ERROR',
    httpStatus,
    // `unrecognizedEnvelope` is the machine-readable signal that the body was
    // NOT a facade error envelope — an edge-runtime worker kill (HTTP 546),
    // proxy, or WAF page. The send-error mapper reads it to show the user
    // actionable copy instead of this diagnostic string, while the string
    // itself stays intact for Sentry/console. `rawBody` remains opt-in for ops
    // dashboards only (never surfaced to toasts/logs — see class doc).
    details: { unrecognizedEnvelope: true, ...(excerpt ? { rawBody: excerpt } : {}) },
  };
}

interface ResponseLike {
  status?: number;
  clone?: () => ResponseLike;
  text: () => Promise<string>;
  headers?: { get: (name: string) => string | null };
}

function isResponseLike(value: unknown): value is ResponseLike {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { text?: unknown }).text === 'function'
  );
}

function parseFacadeEnvelope(bodyText: string): Omit<StructuredFunctionError, 'httpStatus'> | null {
  if (!bodyText) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(bodyText);
  } catch {
    return null;
  }
  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    typeof (parsed as { error?: unknown }).error !== 'string' ||
    typeof (parsed as { code?: unknown }).code !== 'string'
  ) {
    return null;
  }
  const envelope = parsed as {
    error: string;
    code: string;
    provider?: unknown;
    action?: unknown;
    details?: unknown;
  };

  return {
    message: envelope.error,
    code: envelope.code as CorrespondenceErrorCode,
    provider:
      envelope.provider === 'gmail' || envelope.provider === 'outlook'
        ? envelope.provider
        : undefined,
    action:
      typeof envelope.action === 'string' ? (envelope.action as CorrespondenceAction) : undefined,
    details:
      envelope.details && typeof envelope.details === 'object'
        ? (envelope.details as Record<string, unknown>)
        : undefined,
  };
}

function extractHttpStatus(error: unknown): number {
  if (error instanceof AuthApiError && typeof error.status === 'number') {
    return error.status;
  }
  if (error !== null && typeof error === 'object' && 'status' in error) {
    const { status } = error as Record<string, unknown>;
    if (typeof status === 'number') {
      return status;
    }
    if (typeof status === 'string') {
      const parsed = Number.parseInt(status, 10);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
  }
  return 0;
}
